import type { LegalDocument } from 'src/graphql/entities/legal-document/legal-document.types';

type SegmentName = (brandId: string) => string;

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The description under a version in the Version menu, relative to the segment being edited. */
export function describeVersionUsage(
  {
    brandIds,
    updatedAt,
    createdAt,
  }: Pick<LegalDocument, 'brandIds' | 'updatedAt' | 'createdAt'>,
  selectedSegmentId: string,
): string {
  const isOwn = brandIds.includes(selectedSegmentId);
  const others = brandIds.length - (isOwn ? 1 : 0);

  if (isOwn && others) {
    return `Used by this segment and ${others} ${others === 1 ? 'other' : 'others'}`;
  }

  if (isOwn) return 'Used by this segment';

  if (others) return `Used by ${others} other ${others === 1 ? 'segment' : 'segments'}`;

  const updated = new Date(updatedAt ?? createdAt).toLocaleDateString('en-US', {
    dateStyle: 'medium',
  });

  return `Not used · updated ${updated}`;
}

export function describeVersionSharing(
  { brandIds }: Pick<LegalDocument, 'brandIds'>,
  selectedSegmentId: string,
  segmentName: SegmentName,
): string {
  const others = brandIds.filter((brandId) => brandId !== selectedSegmentId);
  const own = segmentName(selectedSegmentId);

  return others.length
    ? `Also used by ${joinNames(others.map(segmentName))}, so edits change it there too. New version gives ${own} its own.`
    : `Only ${own} uses this version.`;
}
